// Whitelist of valid social media platforms
const VALID_SOCIAL_PLATFORMS = [
  'instagram', 'twitter', 'linkedin', 'website', 'email',
  'facebook', 'youtube', 'tiktok', 'threads', 'mastodon', 'bluesky', 'portfolio'
];

const VALID_URL_PROTOCOLS = ['https://', 'http://', 'mailto:'];

// Validate and sanitize social media links to prevent code injection
function validateSocialLinks(socialData) {
  if (!socialData || typeof socialData !== 'object') return {};
  
  const validated = {};
  
  try {
    for (const [platform, url] of Object.entries(socialData)) {
      // Only allow whitelisted platforms
      if (!VALID_SOCIAL_PLATFORMS.includes(platform.toLowerCase())) continue;
      
      // Validate URL is a string
      if (typeof url !== 'string') continue;
      
      // Validate URL protocol
      if (!VALID_URL_PROTOCOLS.some(proto => url.startsWith(proto))) continue;
      
      // Prevent XSS - block dangerous protocols
      if (url.includes('javascript:') || url.includes('data:') || url.includes('vbscript:')) continue;
      
      // Additional validation: ensure it's a safe URL
      try {
        new URL(url);
        validated[platform.toLowerCase()] = url;
      } catch {
        // Invalid URL format, skip
        continue;
      }
    }
  } catch (error) {
    console.warn('Error validating social links:', error);
    return {};
  }
  
  return validated;
}

// Service to fetch and manage shows data from Ghost API
export class ShowsService {
  constructor() {
    this.cache = null;
    this.cacheExpiry = 0;
    this.cacheTimeout = 5 * 60 * 1000;
  }

  // Photographer data mapped by post slug
  photographerMap = {
    'descobridores-do-amor-fotos': 'Natacha Ventura',
    'sangue-de-todas-as-cores-fotos': 'João Garcia Miguel'
    // Add more as needed
  };

  // Get shows from Ghost API with caching
  async getShows() {
    if (this.cache && this.cacheExpiry > Date.now()) {
      return this.cache;
    }

    try {
      // Try to fetch from Ghost API first
      const apiResponse = await this.fetchFromGhostAPI();
      if (Array.isArray(apiResponse) && apiResponse.length > 0) {
        const mergedShows = await this.mergeShowsWithGallerySources(apiResponse);
        this.cache = mergedShows;
        this.cacheExpiry = Date.now() + this.cacheTimeout;
        return mergedShows;
      }
      // If API responds but has no data, fall back to mock data for UX
      console.warn('Ghost API returned no shows; using fallback data');
    } catch (error) {
      console.warn('Ghost API fetch failed, using fallback data:', error);
    }

    // Fallback to mock data if API fails
    const fallbackData = this.getFallbackShows();
    this.cache = fallbackData;
    this.cacheExpiry = Date.now() + this.cacheTimeout;
    return fallbackData;
  }

  // Fetch shows from Ghost API
  async fetchFromGhostAPI() {
    try {
      // Get the base URL (handles both local and production)
      const baseUrl = window.location.origin;

      const fetchAllItems = async (resourceKey, itemKey) => {
        const all = [];
        let page = 1;
        let totalPages = 1;

        do {
          const query = `?key=${this.getContentAPIKey()}&include=tags,authors&fields=id,title,slug,feature_image,excerpt,meta_description,url,published_at,custom_excerpt,custom_template&limit=100&page=${page}`;
          const response = await fetch(`${baseUrl}/ghost/api/content/${resourceKey}/${query}`);

          if (!response.ok) {
            break;
          }

          const data = await response.json();
          const items = Array.isArray(data[itemKey]) ? data[itemKey] : [];
          all.push(...items);

          const pagination = data?.meta?.pagination;
          totalPages = Number.isFinite(pagination?.pages) ? pagination.pages : 1;
          page += 1;
        } while (page <= totalPages);

        return all;
      };

      const [posts, pages] = await Promise.all([
        fetchAllItems('posts', 'posts'),
        fetchAllItems('pages', 'pages')
      ]);
      const allItems = [...posts, ...pages];

      const dedupedItems = allItems.filter((item, index, array) =>
        array.findIndex(other => other.id === item.id) === index
      );

      const filtered = dedupedItems.filter(item => this.isShowContent(item));

      return filtered
        .map(item => this.transformPostToShow(item))
        .filter(show => Boolean(show && show.title && show.url));
    } catch (error) {
      console.error('Error fetching from Ghost API:', error);
      return null;
    }
  }

  normalizeTagSlug(slug = '') {
    return String(slug)
      .toLowerCase()
      .replace(/^hash-/, '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  normalizeShowKey(value = '') {
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  extractPrimaryGalleryImageFromHtml(html = '') {
    if (!html || typeof html !== 'string') {
      return null;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const image = doc.querySelector('img[src]');
      return image ? image.getAttribute('src') : null;
    } catch (error) {
      console.warn('Unable to extract gallery header image from HTML:', error);
      return null;
    }
  }

  extractGalleryImagesFromHtml(html = '') {
    if (!html || typeof html !== 'string') {
      return [];
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      return Array.from(doc.querySelectorAll('img[src]'))
        .map((img) => img.getAttribute('src'))
        .filter(Boolean);
    } catch (error) {
      console.warn('Unable to extract gallery images from HTML:', error);
      return [];
    }
  }

  pickRandomImage(images = []) {
    if (!Array.isArray(images) || images.length === 0) {
      return null;
    }

    const index = Math.floor(Math.random() * images.length);
    return images[index] || null;
  }

  isShowContent(item) {
    const nonShowSlugKeywords = [
      'refund',
      'policy',
      'privacy',
      'terms',
      'cookies',
      'legal',
      'contact',
      'about',
      'mission',
      'resources',
      'podcast',
      'shop'
    ];

    const showLikeTagSlugs = [
      'show',
      'shows',
      'espetaculo',
      'espetaculos',
      'performance',
      'performances',
      'repertorio',
      'teatro'
    ];

    const showStatusTagSlugs = [
      'current',
      'upcoming',
      'archive',
      'past',
      'em-cartaz',
      'brevemente',
      'arquivo'
    ];

    const tags = Array.isArray(item.tags) ? item.tags : [];
    const normalizedTagSlugs = tags.map(tag => this.normalizeTagSlug(tag?.slug));

    const template = String(item.custom_template || '').toLowerCase();
    const title = String(item.title || '').toLowerCase();
    const slug = this.normalizeTagSlug(item.slug || '');
    const customExcerpt = String(item.custom_excerpt || '').toLowerCase();
    const excerpt = String(item.excerpt || '').toLowerCase();

    // Explicitly exclude known listing/index pages (e.g. /shows/ itself).
    const listingPageSlugs = new Set([
      'show',
      'shows',
      'espetaculo',
      'espetaculos',
      'espetaculos-lista',
      'shows-page'
    ]);

    if (listingPageSlugs.has(slug)) {
      return false;
    }

    const isClearlyNonShowPage = nonShowSlugKeywords.some(keyword =>
      slug.includes(keyword) || title.includes(keyword) || template.includes(keyword)
    );

    if (isClearlyNonShowPage) {
      return false;
    }

    const hasShowTag = normalizedTagSlugs.some(slug =>
      showLikeTagSlugs.includes(slug) || /show|espetac/.test(slug)
    );

    const hasShowStatusTag = normalizedTagSlugs.some(slug =>
      showStatusTagSlugs.includes(slug)
    );

    const templateSuggestsShow = /show|espetac/.test(template);
    const titleSuggestsShow = /show|espetac/.test(title);
    const slugSuggestsShow = /show|espetac/.test(slug);

    const parsedMeta = this.parseCustomFields(customExcerpt || excerpt || '');
    const hasCategoryMeta = Boolean(parsedMeta.category);
    const hasOperationalMeta = Boolean(
      parsedMeta.venue ||
      parsedMeta.date ||
      parsedMeta.location ||
      parsedMeta.duration ||
      parsedMeta.director ||
      parsedMeta.cast ||
      parsedMeta.status
    );
    const hasStructuredShowMeta = hasCategoryMeta && hasOperationalMeta;

    // Keep status tags useful, but only in show-like contexts.
    const statusInShowContext = hasShowStatusTag && (hasShowTag || templateSuggestsShow || titleSuggestsShow || slugSuggestsShow || hasStructuredShowMeta);

    return hasShowTag || templateSuggestsShow || titleSuggestsShow || slugSuggestsShow || hasStructuredShowMeta || statusInShowContext;
  }

  async mergeShowsWithGallerySources(shows) {
    const baseShows = Array.isArray(shows) ? shows : [];

    try {
      const galleryPosts = await this.fetchGalleryPostsFromGhostAPI('all');
      if (!Array.isArray(galleryPosts) || galleryPosts.length === 0) {
        return baseShows;
      }

      const merged = [...baseShows];
      const byId = new Set(baseShows.map(show => show.id));
      const bySlug = new Set(baseShows.map(show => this.normalizeShowKey(show.slug || show.title || '')));
      const knownBySlug = new Map(baseShows.map(show => [this.normalizeShowKey(show.slug || ''), show]));

      galleryPosts.forEach((post) => {
        const normalizedPostSlug = this.normalizeShowKey(post.slug || '');
        const normalizedPostTitle = this.normalizeShowKey(post.title || '');
        const tags = Array.isArray(post.tags) ? post.tags : [];
        const galleryImages = this.extractGalleryImagesFromHtml(post.html || '');
        const randomGalleryImage = this.pickRandomImage(galleryImages);

        const matchedShow = tags
          .map(tag => knownBySlug.get(this.normalizeShowKey(tag?.slug || tag?.name || '')))
          .find(Boolean) || knownBySlug.get(normalizedPostSlug);

        if (matchedShow) {
          // Keep the show's own header/feature image as primary.
          // Only fall back to a random gallery image if no feature image exists.
          if (!matchedShow.featureImage && randomGalleryImage) {
            matchedShow.featureImage = randomGalleryImage;
          }

          const matchedKey = this.normalizeShowKey(matchedShow.slug || matchedShow.title || '');
          if (!bySlug.has(matchedKey)) {
            merged.push(matchedShow);
            bySlug.add(matchedKey);
            byId.add(matchedShow.id);
          }
          return;
        }

        // Only derive new show cards from gallery posts that look like real show content.
        if (!this.isShowContent(post)) {
          return;
        }

        const galleryDerivedShow = this.transformPostToShow(post);
        // Prefer post header (feature_image) from transformPostToShow.
        // Use random gallery image only when no header image is available.
        if (!galleryDerivedShow.featureImage && randomGalleryImage) {
          galleryDerivedShow.featureImage = randomGalleryImage;
        }
        galleryDerivedShow.url = galleryDerivedShow.url || `/gallery/?show=${encodeURIComponent(galleryDerivedShow.slug || post.slug || '')}`;

        const derivedKey = this.normalizeShowKey(galleryDerivedShow.slug || galleryDerivedShow.title || normalizedPostTitle);
        if (!derivedKey || byId.has(galleryDerivedShow.id) || bySlug.has(derivedKey)) {
          return;
        }

        merged.push(galleryDerivedShow);
        byId.add(galleryDerivedShow.id);
        bySlug.add(derivedKey);
      });

      return merged;
    } catch (error) {
      console.warn('Unable to merge gallery-derived shows:', error);
      return baseShows;
    }
  }

  // Transform Ghost post to show object
  transformPostToShow(post) {
    // Extract custom fields from excerpt or custom_excerpt
    // Format: category:past|current|upcoming venue:Teatro Nacional date:2024-03-15
    const customData = this.parseCustomFields(post.custom_excerpt || post.excerpt || '');
    
    const normalizedCategory = this.normalizeCategory(customData.category || this.determineCategory(post));
    const normalizedStatus = customData.status || this.getStatusFromCategory(normalizedCategory);

    return {
      id: post.id,
      title: post.title,
      slug: post.slug,
      featureImage: post.feature_image || '',
      excerpt: customData.description || post.excerpt || post.meta_description || '',
      url: post.url,
      publishedAt: post.published_at,
      tags: post.tags || [],
      // Custom fields
      category: normalizedCategory,
      status: normalizedStatus,
      venue: customData.venue || '',
      date: customData.date || '',
      location: customData.location || '',
      duration: customData.duration || '',
      director: customData.director || '',
      cast: customData.cast || '',
      position: Number.isFinite(customData.position) ? customData.position : null,
      // Additional metadata
      published: post.published_at ? new Date(post.published_at) <= new Date() : false
    };
  }

  normalizeCategory(category) {
    const value = String(category || '').toLowerCase();
    if (['past', 'archive', 'arquivo'].includes(value)) return 'archive';
    if (['current', 'em-cartaz', 'em cartaz', 'active'].includes(value)) return 'current';
    if (['upcoming', 'brevemente', 'future', 'futuro'].includes(value)) return 'upcoming';
    return 'current';
  }

  getStatusFromCategory(category) {
    switch (this.normalizeCategory(category)) {
      case 'upcoming':
        return 'Brevemente';
      case 'archive':
        return 'Arquivo';
      case 'current':
      default:
        return 'Em Cartaz';
    }
  }

  // Parse custom fields from post excerpt/custom_excerpt
  // Supports formats:
  // - category: past
  // - venue: Teatro Nacional
  // - date: 2024-03-15
  parseCustomFields(text) {
    const fields = {};
    
    if (!text) return fields;
    
    // Split by newlines or pipes
    const lines = text.split(/[\n|]/);
    const descriptionLines = [];
    
    lines.forEach(line => {
      // Accept unicode keys (e.g. "posicao" / "posição") instead of ASCII-only \w.
      const match = line.match(/^([^:]+):\s*(.+)$/);
      if (match) {
        const [, key, value] = match;
        const normalizedKey = key
          .toLowerCase()
          .trim()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        const normalizedValue = value.trim();
        
        // Map keys
        switch (normalizedKey) {
          case 'position':
          case 'posicao':
          case 'posição':
          case 'order':
          case 'ordem': {
            const parsedPosition = Number.parseInt(normalizedValue, 10);
            if (Number.isFinite(parsedPosition) && parsedPosition > 0) {
              fields.position = parsedPosition;
            }
            break;
          }
          case 'category':
          case 'categoria':
            // Normalize category values
            if (/past|passado|arquivo|archive/i.test(normalizedValue)) {
              fields.category = 'archive';
              fields.status = 'Arquivo';
            } else if (/current|atual|em cartaz|em-cartaz/i.test(normalizedValue)) {
              fields.category = 'current';
              fields.status = 'Em Cartaz';
            } else if (/upcoming|brevemente|futuro/i.test(normalizedValue)) {
              fields.category = 'upcoming';
              fields.status = 'Brevemente';
            }
            break;
          case 'venue':
          case 'local':
          case 'teatro':
            fields.venue = normalizedValue;
            break;
          case 'date':
          case 'data':
            fields.date = normalizedValue;
            break;
          case 'location':
          case 'cidade':
          case 'localização':
            fields.location = normalizedValue;
            break;
          case 'duration':
          case 'duração':
          case 'duracao':
            fields.duration = normalizedValue;
            break;
          case 'director':
          case 'diretor':
          case 'direção':
          case 'direcao':
            fields.director = normalizedValue;
            break;
          case 'cast':
          case 'elenco':
            fields.cast = normalizedValue;
            break;
          case 'description':
          case 'descrição':
          case 'descricao':
            fields.description = normalizedValue;
            break;
          default:
            // If it's not a recognized field, keep it as description text
            descriptionLines.push(line.trim());
        }
      } else if (line.trim()) {
        // If the line doesn't match the field:value pattern, it's description text
        descriptionLines.push(line.trim());
      }
    });
    
    // If no explicit description was set, use the non-field lines as description
    if (!fields.description && descriptionLines.length > 0) {
      fields.description = descriptionLines.join(' ');
    }
    
    return fields;
  }

  // Get Ghost Content API key (you'll need to set this)
  getContentAPIKey() {
    // You can get this from your Ghost admin panel under Integrations
    // For now, we'll try to get it from a meta tag or use environment
    const metaKey = document.querySelector('meta[name="ghost-content-api-key"]');
    if (metaKey) {
      return metaKey.content;
    }
    
    // Fallback - you should set this in your Ghost theme
    return window.ghostContentAPIKey || 'your-content-api-key-here';
  }

  // Determine show status from Ghost post data
  determineStatus(post) {
    const tags = post.tags || [];
    const tagSlugs = tags.map(tag => this.normalizeTagSlug(tag.slug));
    
    // Check for status tags
    if (tagSlugs.includes('current') || tagSlugs.includes('em-cartaz')) {
      return 'Em Cartaz';
    }
    if (tagSlugs.includes('upcoming') || tagSlugs.includes('brevemente')) {
      return 'Brevemente';
    }
    if (tagSlugs.includes('archive') || tagSlugs.includes('arquivo') || tagSlugs.includes('past')) {
      return 'Arquivo';
    }
    
    // Default based on publish date
    const publishDate = new Date(post.published_at);
    const now = new Date();
    const monthsOld = (now - publishDate) / (1000 * 60 * 60 * 24 * 30);
    
    if (monthsOld > 6) {
      return 'Arquivo';
    } else {
      return 'Em Cartaz';
    }
  }

  // Determine category from status
  determineCategory(post) {
    const status = this.determineStatus(post);
    switch (status) {
      case 'Em Cartaz': return 'current';
      case 'Brevemente': return 'upcoming';
      case 'Arquivo': return 'archive';
      default: return 'current';
    }
  }

  // Fallback show data
  getFallbackShows() {
    return [
      {
        id: '1',
        title: 'Sangue de Todas as Cores',
        slug: 'sangue-de-todas-as-cores',
        featureImage: 'https://picsum.photos/600/400?random=10',
        status: 'Em Cartaz',
        excerpt: 'Uma exploração profunda das relações humanas através da cor e emoção.',
        category: 'current',
        url: '/sangue-de-todas-as-cores/',
        publishedAt: '2024-03-01T00:00:00.000Z',
        tags: [{ name: 'Show', slug: 'show' }, { name: 'Current', slug: 'current' }]
      },
      {
        id: '2',
        title: 'História do Coco',
        slug: 'historia-do-coco',
        featureImage: 'https://picsum.photos/600/400?random=11',
        status: 'Brevemente',
        excerpt: 'Uma jornada através da tradição oral e natureza.',
        category: 'upcoming',
        url: '/historia-do-coco/',
        publishedAt: '2024-05-01T00:00:00.000Z',
        tags: [{ name: 'Show', slug: 'show' }, { name: 'Upcoming', slug: 'upcoming' }]
      },
      {
        id: '3',
        title: 'O Jardim das Cerejeiras',
        slug: 'o-jardim-das-cerejeiras',
        featureImage: 'https://picsum.photos/600/400?random=12',
        status: 'Em Cartaz',
        excerpt: 'O clássico de Chekhov em interpretação contemporânea.',
        category: 'current',
        url: '/o-jardim-das-cerejeiras/',
        publishedAt: '2024-02-01T00:00:00.000Z',
        tags: [{ name: 'Show', slug: 'show' }, { name: 'Current', slug: 'current' }]
      },
      {
        id: '4',
        title: 'A Tempestade',
        slug: 'a-tempestade',
        featureImage: 'https://picsum.photos/600/400?random=13',
        status: 'Arquivo',
        excerpt: 'Shakespeare reimaginado para o século XXI.',
        category: 'archive',
        url: '/a-tempestade/',
        publishedAt: '2023-06-01T00:00:00.000Z',
        tags: [{ name: 'Show', slug: 'show' }, { name: 'Archive', slug: 'archive' }]
      },
      {
        id: '5',
        title: 'Hamlet',
        slug: 'hamlet',
        featureImage: 'https://picsum.photos/600/400?random=14',
        status: 'Em Cartaz',
        excerpt: 'A tragédia eterna de Shakespeare.',
        category: 'current',
        url: '/hamlet/',
        publishedAt: '2024-01-01T00:00:00.000Z',
        tags: [{ name: 'Show', slug: 'show' }, { name: 'Current', slug: 'current' }]
      },
      {
        id: '6',
        title: 'Romeu e Julieta',
        slug: 'romeu-e-julieta',
        featureImage: 'https://picsum.photos/600/400?random=15',
        status: 'Brevemente',
        excerpt: 'A história de amor mais famosa do mundo.',
        category: 'upcoming',
        url: '/romeu-e-julieta/',
        publishedAt: '2024-07-01T00:00:00.000Z',
        tags: [{ name: 'Show', slug: 'show' }, { name: 'Upcoming', slug: 'upcoming' }]
      },
      {
        id: '7',
        title: 'Macbeth',
        slug: 'macbeth',
        featureImage: 'https://picsum.photos/600/400?random=16',
        status: 'Arquivo',
        excerpt: 'A ambição e suas consequências.',
        category: 'archive',
        url: '/macbeth/',
        publishedAt: '2023-09-01T00:00:00.000Z',
        tags: [{ name: 'Show', slug: 'show' }, { name: 'Archive', slug: 'archive' }]
      },
      {
        id: '8',
        title: 'Rei Lear',
        slug: 'rei-lear',
        featureImage: 'https://picsum.photos/600/400?random=17',
        status: 'Arquivo',
        excerpt: 'Poder, família e loucura.',
        category: 'archive',
        url: '/rei-lear/',
        publishedAt: '2023-03-01T00:00:00.000Z',
        tags: [{ name: 'Show', slug: 'show' }, { name: 'Archive', slug: 'archive' }]
      },
      {
        id: '9',
        title: 'Otelo',
        slug: 'otelo',
        featureImage: 'https://picsum.photos/600/400?random=18',
        status: 'Arquivo',
        excerpt: 'Ciúme e traição.',
        category: 'archive',
        url: '/otelo/',
        publishedAt: '2023-11-01T00:00:00.000Z',
        tags: [{ name: 'Show', slug: 'show' }, { name: 'Archive', slug: 'archive' }]
      }
    ];
  }

  // Fetch gallery posts from Ghost API
  async fetchGalleryPostsFromGhostAPI(limit = 'all') {
    try {
      const baseUrl = window.location.origin;
      // Fetch posts tagged for gallery (supports both internal #gallery and public gallery tags)
      // We need 'html' to extract images and 'tags' to link to shows
      const response = await fetch(`${baseUrl}/ghost/api/content/posts/?key=${this.getContentAPIKey()}&filter=tag:hash-gallery,tag:gallery&include=tags,html&limit=${limit}`);

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      console.log('DEBUG: fetchGalleryPostsFromGhostAPI found posts:', data.posts.length);
      
      // Log first post structure
      if (data.posts.length > 0) {
        const firstPost = data.posts[0];
        console.log('DEBUG: First post slug:', firstPost.slug);
        console.log('DEBUG: First post has html?', !!firstPost.html);
        console.log('DEBUG: First post html length:', firstPost.html?.length || 0);
        console.log('DEBUG: First 200 chars of html:', firstPost.html?.substring(0, 200) || 'NO HTML');
      }
      
      return data.posts;
    } catch (error) {
      console.warn('Error fetching gallery posts from Ghost API:', error);
      return null;
    }
  }

  // Get gallery images for a specific show
  async getGalleryImages(limit = 'all') {
    const shows = await this.getShows();
    let galleryImages = [];
    let usedRealData = false;

    // Try to fetch real gallery data from Ghost
    try {
      const galleryPosts = await this.fetchGalleryPostsFromGhostAPI(limit);

      if (galleryPosts && galleryPosts.length > 0) {
        usedRealData = true;
        const parser = new DOMParser();

        galleryPosts.forEach(post => {
          const tags = Array.isArray(post.tags) ? post.tags : [];

          // Resolve associated show flexibly:
          // 1) exact tag->show slug match
          // 2) normalized tag->show slug match (handles punctuation differences)
          // 3) post slug itself as a show slug
          // 4) fallback to post metadata so images still render
          const normalize = (value = '') => String(value)
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]/g, '');

          const showByTagExact = tags
            .map(tag => shows.find(s => s.slug === tag.slug))
            .find(Boolean);

          const showByTagNormalized = tags
            .map(tag => shows.find(s => normalize(s.slug) === normalize(tag.slug)))
            .find(Boolean);

          const showByPostSlug = shows.find(s => normalize(s.slug) === normalize(post.slug));

          const show = showByTagExact || showByTagNormalized || showByPostSlug || {
            id: post.id,
            slug: post.slug,
            title: post.title || 'Show',
            featureImage: post.feature_image || '',
            url: post.url || '',
            tags: tags
          };
            
            const doc = parser.parseFromString(post.html, 'text/html');
            const images = doc.querySelectorAll('img');

            images.forEach((img, index) => {
              let title = '';
              let order = index;
              
              const figure = img.closest('figure');
              if (figure) {
                const figcaption = figure.querySelector('figcaption');
                if (figcaption) {
                  title = figcaption.textContent.trim();
                  const orderAttr = figcaption.getAttribute('data-order');
                  if (orderAttr && !isNaN(parseInt(orderAttr))) {
                    order = parseInt(orderAttr);
                  }
                }
              }
              
              if (!title) title = `${show.title} - Image ${index + 1}`;

              // Use alt attribute as photographer/photography credit
              const photographer = img.getAttribute('alt') || '';

              // Category detection with mapping
              let category = 'performance';
              const categoryMap = {
                'rehearsal': 'rehearsal',
                'ensaio': 'rehearsal',
                'backstage': 'backstage',
                'bastidores': 'backstage',
                'cast': 'cast',
                'elenco': 'cast',
                'production': 'production',
                'producao': 'production'
              };
              
              const foundCategoryTag = post.tags.find(t => {
                const slug = t.slug.replace('hash-', '');
                return categoryMap.hasOwnProperty(slug);
              });
              
              if (foundCategoryTag) {
                const slug = foundCategoryTag.slug.replace('hash-', '');
                category = categoryMap[slug] || slug;
              }

              galleryImages.push({
                id: `gallery-${post.id}-${index}`,
                src: img.getAttribute('src'),
                alt: show.title,
                title: title,
                photographer: photographer,
                order: order,
                show: show.slug,
                showName: show.title,
                category: category,
                showData: show
              });
            });
        });
      }
    } catch (e) {
      console.error("Error processing gallery images", e);
    }

    if (usedRealData && galleryImages.length > 0) {
      console.log('DEBUG: Using REAL gallery data, found', galleryImages.length, 'images');
      return galleryImages;
    }
    
    // Fallback: generate sample gallery data based on available shows
    console.log('DEBUG: No real gallery data found, using FALLBACK mock data');

    if (Array.isArray(shows) && shows.length > 0) {
      shows.forEach((show) => {
        // For each show, generate 1-3 sample images
        const perShowCount = Math.floor(Math.random() * 3) + 1;
        for (let i = 0; i < perShowCount; i++) {
          galleryImages.push({
            id: `fallback-${show.id || show.slug}-${i}`,
            src: show.featureImage || `https://picsum.photos/600/400?random=${Math.floor(Math.random() * 100)}`,
            alt: `${show.title || show.showName || 'Show'} - Image ${i + 1}`,
            title: this.getImageTitle(i),
            photographer: show.photographer || '',
            order: i,
            show: show.slug || show.id || `show-${i}`,
            showName: show.title || show.showName || show.slug || '',
            category: this.getImageCategory(i),
            showData: show // Include the full show data
          });
        }
      });
    }

    return galleryImages;
  }

  // Generate image titles
  getImageTitle(index) {
    const titles = [
      'Opening Night',
      'Behind the Scenes', 
      'Cast Photo',
      'Rehearsal',
      'Set Design',
      'Costume Fitting',
      'Final Act',
      'Director\'s Notes',
      'Technical Rehearsal',
      'Dress Rehearsals'
    ];
    return titles[index % titles.length];
  }

  // Generate image categories
  getImageCategory(index) {
    const categories = ['performance', 'rehearsal', 'backstage', 'cast', 'production'];
    return categories[index % categories.length];
  }
}

// Create a singleton instance
export const showsService = new ShowsService();